import { useState, useEffect, useRef } from "react";
import { useAuth, BACKEND_URL } from "@/App";
import { toast } from "sonner";
import { 
  Image, Upload, Loader2, Trash2, Plus, Edit2, Save, X, 
  Camera, Eye, Calendar
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const GalleryManager = () => {
  const { authAxios } = useAuth();
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [uploadData, setUploadData] = useState({ title: "", caption: "" });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await authAxios.get("/gallery");
      setImages(response.data);
    } catch (e) {
      console.error("Failed to fetch gallery:", e);
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (uploadData.title) formData.append("title", uploadData.title);
      if (uploadData.caption) formData.append("caption", uploadData.caption);

      const response = await authAxios.post("/gallery/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Image uploaded successfully!");
      setImages([response.data, ...images]);
      setShowUploadModal(false);
      setUploadData({ title: "", caption: "" });
    } catch (e) {
      console.error("Upload error:", e);
      toast.error(e.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (imageId) => {
    try {
      const response = await authAxios.delete(`/gallery/${imageId}`);
      console.log("Delete response:", response);
      setImages(images.filter(img => img.id !== imageId));
      toast.success("Image deleted successfully!");
      setDeleteConfirm(null);
    } catch (e) {
      console.error("Delete error:", e.response || e);
      toast.error(e.response?.data?.detail || "Failed to delete image");
    }
  };

  const getImageUrl = (url) => {
    if (!url) return "";
    return url.startsWith("/") ? `${BACKEND_URL}${url}` : url;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-neon-pink animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-orbitron text-2xl text-white flex items-center gap-3">
            <Camera className="w-6 h-6 text-neon-pink" />
            GRIND GALLERY MANAGER
          </h2>
          <p className="font-rajdhani text-gray-400 mt-1">
            {images.length} image{images.length !== 1 ? "s" : ""} in gallery
          </p>
        </div>
        
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-neon-pink to-purple-600 hover:from-neon-pink/80 hover:to-purple-600/80 text-white font-orbitron text-sm rounded-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          ADD IMAGE
        </button>
      </div>

      {/* Images Grid */}
      {images.length === 0 ? (
        <div className="text-center py-20 cyber-card">
          <Image className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500 text-lg">No images in gallery</p>
          <p className="font-rajdhani text-gray-600 text-sm mt-2">
            Click "ADD IMAGE" to upload your first photo
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {images.map((image) => (
            <div 
              key={image.id} 
              className="cyber-card overflow-hidden group"
            >
              {/* Image Preview */}
              <div className="relative aspect-square bg-gray-800">
                <img
                  src={getImageUrl(image.image_url)}
                  alt={image.title || "Gallery image"}
                  className="w-full h-full object-cover"
                />
                
                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <a
                    href={getImageUrl(image.image_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-neon-blue/80 hover:bg-neon-blue rounded-full transition-colors"
                  >
                    <Eye className="w-5 h-5 text-white" />
                  </a>
                  <button
                    onClick={() => setDeleteConfirm(image)}
                    className="p-3 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors"
                  >
                    <Trash2 className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Image Info */}
              <div className="p-4 space-y-2">
                <h3 className="font-orbitron text-sm text-white truncate">
                  {image.title || "Untitled"}
                </h3>
                {image.caption && (
                  <p className="font-rajdhani text-xs text-gray-400 line-clamp-2">
                    {image.caption}
                  </p>
                )}
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Calendar className="w-3 h-3" />
                  <span>{formatDate(image.created_at)}</span>
                </div>
              </div>

              {/* Quick Delete Button */}
              <div className="px-4 pb-4">
                <button
                  onClick={() => setDeleteConfirm(image)}
                  className="w-full py-2 bg-red-500/20 hover:bg-red-500/40 border border-red-500/50 rounded text-red-400 font-orbitron text-xs flex items-center justify-center gap-2 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  DELETE IMAGE
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="bg-gray-900 border-neon-pink/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-orbitron text-neon-pink flex items-center gap-2">
              <Upload className="w-5 h-5" />
              UPLOAD NEW IMAGE
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="font-rajdhani text-sm text-gray-400 block mb-1">
                Title (Optional)
              </label>
              <input
                type="text"
                value={uploadData.title}
                onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                placeholder="e.g., Game Day Warm-ups"
                className="w-full bg-gray-800 border border-white/10 rounded-lg px-4 py-3 text-white font-rajdhani focus:border-neon-pink/50 focus:outline-none"
              />
            </div>

            <div>
              <label className="font-rajdhani text-sm text-gray-400 block mb-1">
                Caption (Optional)
              </label>
              <textarea
                value={uploadData.caption}
                onChange={(e) => setUploadData({ ...uploadData, caption: e.target.value })}
                placeholder="Describe the moment..."
                rows={3}
                className="w-full bg-gray-800 border border-white/10 rounded-lg px-4 py-3 text-white font-rajdhani focus:border-neon-pink/50 focus:outline-none resize-none"
              />
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              id="admin-gallery-upload"
            />

            <label
              htmlFor="admin-gallery-upload"
              className={`flex items-center justify-center gap-3 w-full py-4 border-2 border-dashed border-neon-pink/50 hover:border-neon-pink rounded-xl cursor-pointer transition-all ${uploading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-6 h-6 text-neon-pink animate-spin" />
                  <span className="font-orbitron text-neon-pink">UPLOADING...</span>
                </>
              ) : (
                <>
                  <Camera className="w-6 h-6 text-neon-pink" />
                  <span className="font-orbitron text-neon-pink">SELECT IMAGE</span>
                </>
              )}
            </label>

            <p className="text-center text-xs text-gray-500">
              Supports JPG, PNG, WebP • Max 10MB
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-gray-900 border-red-500/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-orbitron text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              CONFIRM DELETE
            </DialogTitle>
          </DialogHeader>
          
          {deleteConfirm && (
            <div className="space-y-4 mt-4">
              <div className="aspect-video bg-gray-800 rounded-lg overflow-hidden">
                <img
                  src={getImageUrl(deleteConfirm.image_url)}
                  alt={deleteConfirm.title || "Gallery image"}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <p className="font-rajdhani text-gray-300 text-center">
                Are you sure you want to delete{" "}
                <span className="text-white font-bold">
                  "{deleteConfirm.title || "this image"}"
                </span>
                ? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white font-orbitron text-sm rounded-lg transition-colors"
                >
                  CANCEL
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white font-orbitron text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  DELETE
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryManager;
